import React from 'react';
import { CreditCard as Edit, Trash, Wrench, User, MapPin, Calendar, AlertTriangle, ExternalLink, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Tool } from '../../types';
import Badge from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';

interface ToolCardProps {
  tool: Tool;
  onDelete: (id: string) => void;
}

const ToolCard: React.FC<ToolCardProps> = ({ tool, onDelete }) => {
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'in_use': return 'primary';
      case 'maintenance': return 'warning';
      case 'retired': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available': return 'Available';
      case 'in_use': return 'In Use';
      case 'maintenance': return 'Maintenance';
      case 'retired': return 'Retired';
      default: return status;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isMaintenanceDue = () => {
    if (!tool.last_maintenance) return false;
    const lastMaintenance = new Date(tool.last_maintenance);
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    return lastMaintenance < threeMonthsAgo;
  };

  return (
    <Card className="h-full flex flex-col transition-all duration-200 hover:shadow-md group">
      <Link to={`/tools/${tool.id}`} className="flex-grow">
        <div className="relative aspect-[4/3] overflow-hidden rounded-t-lg">
          {tool.images && tool.images.length > 0 ? (
            <img
              src={tool.images[0]}
              alt={tool.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
              <div className="text-center">
                <Wrench className="h-16 w-16 text-blue-600 mx-auto mb-2 group-hover:scale-110 transition-transform duration-200" />
                <p className="text-sm font-medium text-blue-800">{tool.category || 'Tool'}</p>
              </div>
            </div>
          )}
          
          <div className="absolute top-2 right-2 space-x-1">
            <Badge variant={getStatusBadgeVariant(tool.status)}>
              {getStatusLabel(tool.status)}
            </Badge>
            {tool.images && tool.images.length > 1 && (
              <Badge variant="secondary">
                <Camera className="h-3 w-3 mr-1" />
                {tool.images.length}
              </Badge>
            )}
            {isMaintenanceDue() && (
              <Badge variant="warning">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Maintenance Due
              </Badge>
            )}
          </div>
        </div>
        
        <CardContent className="flex-1 flex flex-col p-4">
          <div className="mb-2">
            {tool.category && (
              <Badge variant="secondary">{tool.category}</Badge>
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-blue-600">
            {tool.name}
          </h3>
          <p className="text-sm text-gray-500 line-clamp-2 mb-3">{tool.description}</p>
          
          <div className="mt-auto space-y-2">
            <div className="flex items-center text-sm text-gray-500">
              <MapPin className="h-4 w-4 mr-2" />
              <span className="truncate">{tool.location}</span>
            </div>
            
            {tool.project_code && (
              <div className="flex items-center text-sm text-gray-500">
                <span className="font-medium">Project:</span>
                <span className="ml-1 text-blue-600">{tool.project_code}</span>
              </div>
            )}
            
            {tool.assigned_to && (
              <div className="flex items-center text-sm text-gray-500">
                <User className="h-4 w-4 mr-2" />
                <span className="truncate">{tool.assigned_to}</span>
              </div>
            )}
            
            {tool.serial_number && (
              <div className="flex items-center text-sm text-gray-500">
                <span className="font-medium">S/N:</span>
                <span className="ml-1 font-mono text-xs">{tool.serial_number}</span>
              </div>
            )}
            
            {tool.last_maintenance && (
              <div className="flex items-center text-sm text-gray-500">
                <Calendar className="h-4 w-4 mr-2" />
                <span>Last Maintenance: {formatDate(tool.last_maintenance)}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Link>
      
      <div className="px-4 pb-4 flex gap-2">
        <Link to={`/tools/${tool.id}`} className="flex-1">
          <Button variant="secondary" fullWidth>
            <ExternalLink className="h-4 w-4 mr-1" />
            View Details
          </Button>
        </Link>
        <Button 
          variant="ghost" 
          onClick={() => onDelete(tool.id)}
          className="text-red-600 hover:text-red-700"
        >
          <Trash className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
};

export default ToolCard;